import {Injectable} from '@angular/core'
import {preventBgoProprietaryHttpInterceptorHeader} from '../../core/http/prevent.interceptor'
import {HttpClient} from '@angular/common/http'
import {Observable, of} from 'rxjs'
import {FileModel} from './file.model'
import {map, mergeMap} from 'rxjs/operators'
import {ValidatorFn} from '@angular/forms'
import {ToastrService} from 'ngx-toastr'
import {I18nService} from '../i18n/i18n.service'
import {
  FileUploadError,
  FileUploadRequest,
  GenerateFileUploadLinkFunction,
  isFileUploadError,
} from './file.service.types'
import {Logger} from '../../core/logging/logger.types'

@Injectable({
  providedIn: 'root',
})
export class FileService {
  constructor(
    private readonly http: HttpClient,
    private readonly toastr: ToastrService,
    private readonly i18n: I18nService,
    private readonly logger: Logger,
  ) {}

  getDimensionsTimeout = 2000 // in ms

  static extensions(mimeTypes: string[]): string[] {
    return mimeTypes.map(t => this.extension(t)).filter((e): e is string => !!e)
  }

  static extension(mimeType: string): string | undefined {
    switch (mimeType) {
      case 'application/pdf':
        return 'pdf'
      case 'image/jpg':
        return 'jpg'
      case 'image/jpeg':
        return 'jpeg'
      case 'image/png':
        return 'png'
      case 'image/webp':
        return 'webp'
      case 'image/avif':
        return 'avif'
    }
  }

  static uploadRequired(): ValidatorFn {
    return control => {
      const files = control.value as FileModel[]

      if (!files?.length) {
        return {required: true}
      }

      if (files.every(f => !f.uploaded)) {
        return {required: true}
      }

      return null
    }
  }

  async parseBlob(file: File): Promise<FileModel> {
    const url = URL.createObjectURL(file)
    let dimensions: {width: number; height: number} | undefined
    if (file.type.startsWith('image/')) {
      try {
        dimensions = await this.getImageDimensions(url)
      } catch (err) {
        this.logger.error(`Couldn't get image dimensions.`, {err})
      }
    }
    return {
      fileKey: file.name,
      originalFilename: file.name,
      url,
      revokeUrlOnLoad: true,
      contentType: file.type,
      uploaded: false,
      dimensions,
    }
  }

  uploadFile(
    file: FileModel,
    blob: File,
    generateSignedUploadLink: GenerateFileUploadLinkFunction,
  ): Observable<FileModel> {
    const request: FileUploadRequest = {
      fileName: blob.name,
      contentLength: blob.size,
      contentType: blob.type,
    }
    return generateSignedUploadLink(request).pipe(
      mergeMap(res => {
        if (isFileUploadError(res)) {
          this.notifyUploadError(res)
          return of(res)
        } else {
          return this.uploadFileData(res.uploadLink, blob).pipe(
            // return the file upload info to the caller
            map(() => res),
          )
        }
      }),
      map(res => {
        if (isFileUploadError(res)) {
          file.error = true
          file.uploaded = false
        } else {
          file.fileKey = res.fileKey
          file.url = res.downloadLink
          file.error = undefined
          file.uploaded = true
        }
        return {...file} // return a new object to change object reference
      }),
    )
  }

  private getImageDimensions(imageUrl: string): Promise<{width: number; height: number}> {
    const getDimensions = new Promise<{width: number; height: number}>((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        resolve({width: img.naturalWidth, height: img.naturalHeight})
      }
      img.onerror = reject
      img.src = imageUrl
    })
    const timeout = new Promise<{width: number; height: number}>((_, reject) =>
      setTimeout(() => reject(new Error('Calculating image dimensions timed out')), this.getDimensionsTimeout),
    )

    // if dimensions take too much time, reject with timeout
    return Promise.race([getDimensions, timeout])
  }

  private uploadFileData(url: string, blob: File): Observable<void> {
    return this.http.put<void>(url, blob, {
      reportProgress: true,
      headers: {
        ...preventBgoProprietaryHttpInterceptorHeader,
      },
    })
  }

  private notifyUploadError(error: FileUploadError): void {
    if (error.cause.__typename === 'FileTooBigError') {
      this.toastr.error(
        this.i18n.translate('errors.fileTooBig', {
          maxSize: error.cause.maxContentLength / 1e6,
        }),
        undefined,
        {timeOut: 10000},
      )
    } else if (error.cause.__typename === 'UnsupportedFileTypeError') {
      const supportedContentTypes = error.cause.supportedContentTypes
      const supportedExtensions = FileService.extensions(supportedContentTypes)
        .filter((e): e is string => !!e)
        .join(', ')

      this.toastr.error(
        this.i18n.translate('errors.fileTypeNotSupportedV2', {
          extensions: supportedExtensions,
        }),
        undefined,
        {timeOut: 10000},
      )
    } else {
      // global error handler will handle
      throw error
    }
  }
}
